import React, { useCallback, useEffect, useState } from 'react';
import apiService from '../../services/api';
import { useToast } from '../../components/common';
import './DataUpdates.css';

// Data updates: an app moving a person's records to another schema. The person is
// asked only when more apps could see them afterwards, or something they wrote is
// removed; otherwise it shows under Done with Undo. Plain words throughout: the
// records by their own name ("notes"), the apps by name, never a schema. An app
// updating several kinds of record at once asks once, on one card.

const list = (names) => names.join(', ');
const and = (words) => (words.length < 2 ? words.join('')
  : `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`);

function byApp(updates) {
  const groups = new Map();
  updates.forEach((u) => groups.set(u.app, [...(groups.get(u.app) || []), u]));
  return [...groups.entries()].map(([app, items]) => ({ app, items }));
}

function Details({ u }) {
  return (
    <>
      <div className="du-line">{u.records} {u.thing}</div>
      {u.who_can_see_after.length > 0 && (
        <div className="du-line">
          <span className="du-label">Who can see them after</span>
          {u.who_can_see_after.map((name, i) => (
            <span key={name} className={u.newly_readable.includes(name) ? 'du-new' : undefined}>
              {i ? ', ' : ''}{name}
            </span>
          ))}
        </div>
      )}
      {u.removed.length > 0 && (
        <div className="du-line">
          <span className="du-label">Removed</span>
          {list(u.removed.map((r) => `${r.label} (on ${r.records})`))}
        </div>
      )}
    </>
  );
}

function WaitingCard({ app, items, onAccept, onDecline, busy }) {
  return (
    <div className="du-card">
      <div className="du-title">{app} wants to update your {and(items.map((u) => u.thing))}</div>
      {items.map((u) => (
        <div key={u.id} className={items.length > 1 ? 'du-item' : undefined}><Details u={u} /></div>
      ))}
      <div className="du-actions">
        <button className="btn du-primary" disabled={busy} onClick={() => onAccept(items)}>
          {items.length > 1 ? 'Update all' : 'Update'}
        </button>
        <button className="btn du-quiet" disabled={busy} onClick={() => onDecline(items)}>Not now</button>
      </div>
    </div>
  );
}

function DoneRow({ u, onUndo, busy }) {
  const said = {
    applied: `${u.app} updated ${u.records} ${u.thing}.`,
    declined: `You kept your ${u.thing} as they were.`,
    undone: `${u.app}'s update to your ${u.thing} was undone.`,
  }[u.status];
  return (
    <div className="du-done">
      <span>{said}</span>
      {u.can_undo && <button className="btn du-quiet" disabled={busy} onClick={() => onUndo(u)}>Undo</button>}
    </div>
  );
}

export default function DataUpdates() {
  const toast = useToast();
  const [data, setData] = useState({ waiting: [], done: [] });
  const [busy, setBusy] = useState(null);
  const [showDone, setShowDone] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await apiService.getUpdates();
      setData({ waiting: res?.waiting || [], done: res?.done || [] });
    } catch {
      setData({ waiting: [], done: [] });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const act = async (items, call) => {
    setBusy(items[0].id);
    try {
      for (const u of items) await call(u.id);
      await load();
    } catch (e) {
      toast.error(e?.message || 'That did not go through');
    } finally {
      setBusy(null);
    }
  };

  if (!data.waiting.length && !data.done.length) return null;

  return (
    <div className="du-block">
      <div className="du-head">Updates</div>
      {byApp(data.waiting).map(({ app, items }) => (
        <WaitingCard key={app} app={app} items={items} busy={busy === items[0].id}
          onAccept={(x) => act(x, apiService.acceptUpdate.bind(apiService))}
          onDecline={(x) => act(x, apiService.declineUpdate.bind(apiService))} />
      ))}
      {data.done.length > 0 && (
        <button className="btn du-quiet du-toggle" onClick={() => setShowDone((v) => !v)}>
          Done ({data.done.length})
        </button>
      )}
      {showDone && data.done.map((u) => (
        <DoneRow key={u.id} u={u} busy={busy === u.id}
          onUndo={(x) => act([x], apiService.undoUpdate.bind(apiService))} />
      ))}
    </div>
  );
}
